/**
 * Part 14 — OMS ↔ Xero export lifecycle / idempotency.
 * Part 16 — incomplete post-create steps still count as pending for resume.
 *
 * Retains:
 *   OMS Invoice  ↔ Xero Bill ID      (xeroInvoiceType: Invoice)
 *   OMS Credit Note ↔ Xero Credit ID (xeroInvoiceType: CreditNote)
 *
 * Retrying an export must not create a second Xero document.
 */

import {
  isXeroExportFullyComplete,
  needsXeroExportResume,
  readXeroExportSteps,
} from './xeroExportResume'

export function isCreditNoteFinanceDocument(document = {}) {
  const type = String(document?.documentType || 'invoice')
    .trim()
    .toLowerCase()
  return type === 'creditnote' || type === 'credit_note'
}

/**
 * True when this OMS finance document already has a Xero id (authoritative).
 * Status alone is not enough — a lost id must remain retryable with the same
 * Idempotency-Key.
 */
export function isFinanceDocumentExportedToXero(document = {}) {
  return Boolean(String(document?.xeroInvoiceId || '').trim())
}

/**
 * OMS ↔ Xero link for a finance document.
 * @returns {{ omsDocumentId: string, documentType: string, xeroId: string, xeroType: 'Invoice'|'CreditNote' }|null}
 */
export function getOmsXeroDocumentLink(document = {}) {
  const xeroId = String(document?.xeroInvoiceId || '').trim()
  if (!xeroId) return null
  const isCredit = isCreditNoteFinanceDocument(document)
  const storedType = String(document?.xeroInvoiceType || '').trim()
  const xeroType =
    storedType === 'CreditNote' || storedType === 'Invoice'
      ? storedType
      : isCredit
        ? 'CreditNote'
        : 'Invoice'
  return {
    omsDocumentId: String(document?._id || document?.id || '').trim() || null,
    documentType: isCredit ? 'creditNote' : 'invoice',
    xeroId,
    xeroType,
  }
}

/**
 * Stable Xero Idempotency-Key (≤128 chars) for create Invoice / CreditNote.
 * Same OMS document always yields the same key → safe retries.
 */
export function buildXeroExportIdempotencyKey({
  venueId = '',
  documentId = '',
  orderIds = [],
  isCreditNote = false,
} = {}) {
  const doc = String(documentId || '').trim()
  if (doc) {
    return `oms-${isCreditNote ? 'cn' : 'bill'}-${doc}`.slice(0, 128)
  }
  const orders = (Array.isArray(orderIds) ? orderIds : [])
    .map(id => String(id || '').trim())
    .filter(Boolean)
    .sort()
    .join('_')
  const venue = String(venueId || '').trim()
  const base = `oms-${isCreditNote ? 'cn' : 'bill'}-${venue || 'v'}-${orders || 'none'}`
  return base.slice(0, 128)
}

/**
 * Documents still needing Xero work:
 * - never created yet, OR
 * - created but post-create steps incomplete (Part 16 resume)
 *
 * Invoice and credit notes are independent — exporting a bill does not
 * mark CNs sent, and vice versa.
 */
export function partitionPendingXeroExports(financeDocuments = []) {
  const invoices = []
  const creditNotes = []
  const exportedInvoices = []
  const exportedCreditNotes = []
  const resumeExports = []

  for (const doc of Array.isArray(financeDocuments) ? financeDocuments : []) {
    if (!doc || doc.isArchived) continue
    const link = getOmsXeroDocumentLink(doc)
    const isCredit = isCreditNoteFinanceDocument(doc)
    const resumeOpts = {
      isCreditNote: isCredit,
      expectAuditAttachment: true,
      expectHistoryNote: true,
    }

    if (link && isXeroExportFullyComplete(doc, resumeOpts)) {
      if (isCredit) exportedCreditNotes.push({ document: doc, link })
      else exportedInvoices.push({ document: doc, link })
      continue
    }

    if (link && needsXeroExportResume(doc, resumeOpts)) {
      resumeExports.push({
        document: doc,
        link,
        steps: readXeroExportSteps(doc),
      })
      // Keep in pending lists so planXeroExportJobs queues a resume job.
      if (isCredit) creditNotes.push(doc)
      else invoices.push(doc)
      continue
    }

    if (isCredit) creditNotes.push(doc)
    else invoices.push(doc)
  }

  return {
    pendingInvoices: invoices,
    pendingCreditNotes: creditNotes,
    exportedInvoices,
    exportedCreditNotes,
    resumeExports,
  }
}

/**
 * Part 15 — late credit note after bill already exported.
 * Returns true when the invoice bill must be left untouched and only a new
 * Xero credit note should be created.
 */
export function shouldExportLateCreditAgainstPreservedBill({
  invoiceDocument = null,
  creditNoteDocument = null,
} = {}) {
  if (!creditNoteDocument || !isCreditNoteFinanceDocument(creditNoteDocument)) {
    return false
  }
  if (isFinanceDocumentExportedToXero(creditNoteDocument)) {
    return false
  }
  return isFinanceDocumentExportedToXero(invoiceDocument)
}

/**
 * Resolve Xero bill id to allocate a credit note against.
 * Prefer soft-linked OMS invoice's xero id, then order bill id, then siblings.
 */
export function resolveBillXeroIdForCreditAllocation({
  linkedInvoice = null,
  order = {},
  siblingInvoices = [],
  fallbackXeroInvoiceId = null,
} = {}) {
  const fromLinked = String(linkedInvoice?.xeroInvoiceId || '').trim()
  if (
    fromLinked &&
    linkedInvoice?.xeroInvoiceType !== 'CreditNote' &&
    !isCreditNoteFinanceDocument(linkedInvoice)
  ) {
    return fromLinked
  }

  const fromOrder = String(order?.xeroInvoiceId || '').trim()
  if (fromOrder && order?.xeroInvoiceType !== 'CreditNote') {
    return fromOrder
  }

  for (const doc of Array.isArray(siblingInvoices) ? siblingInvoices : []) {
    if (isCreditNoteFinanceDocument(doc)) continue
    const id = String(doc?.xeroInvoiceId || '').trim()
    if (id && doc?.xeroInvoiceType !== 'CreditNote') return id
  }

  return String(fallbackXeroInvoiceId || '').trim() || null
}
